import { useState, useEffect, useCallback } from 'react';
import { Box, Tooltip } from '@mui/material';
import { GridCellParams, GridColDef, GridRowSelectionModel, GridSortModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceMissionOptions } from 'src/shared/models/options/invoiceMissionOptions';
import invoiceMissionService from 'src/shared/services/invoiceMissionService';
import { InvoiceMissionApproval, InvoiceMissionDataList, InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { useGlobals } from 'src/shared/contexts/appContext';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceSendingTypeStringEnum } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { Warning, Error, CheckCircle, Cancel, WatchLater } from '@mui/icons-material';
import { FSVersionEnum } from 'src/shared/models/enums/FSVersionEnum';

type InvoiceMissionDataGridProps = {
  loadingCallback: (loading: boolean) => void;
  searchString: string;
  ownerId?: number;
};

const InvoiceMissionDataGrid = (props: InvoiceMissionDataGridProps) => {
  const { loadingCallback, searchString, ownerId } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const { dateStamp } = useGlobals();
  const [rows, setRows] = useState<InvoiceMissionResponse[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'date', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    loadingCallback(true);
    try {
      const opts = new InvoiceMissionOptions();
      opts.filter = searchString;
      opts.pageIndex = data.page;
      opts.pageSize = data.pageSize;
      opts.sortCol = sortModel[0].field;
      opts.sortDir = sortModel[0].sort ?? 'invoiceMission_id';
      opts.ownerId = ownerId || undefined;
      const result = await invoiceMissionService.getAll(opts);
      const invoiceResult = extendObjectWithId(result.data.results, 'invoiceMission_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);
        // ConsoleHelper.log(invoiceResult, 'invoiceResult');
        setTimeout(() => {
          setRows(invoiceResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        loadingCallback(false);
        updateData('loading', false);
      }
    }
  }, [loadingCallback, searchString, data.page, data.pageSize, sortModel, ownerId, isMounted]);

  useEffect(() => {
    fetchData();
  }, [fetchData, dateStamp]);

  const onRowClick = (params) => {
    navigate(`${RoutesEnum.InvoiceMissionDetailSendingType}/${params.row.invoiceMission_id}/${InvoiceSendingTypeIntegerEnum.NotSet}`);
  };

  const renderMissionStatusIcon = (approval: InvoiceMissionApproval, totalSent: number, totalCount: number, source: FSVersionEnum) => {
    if (!approval || source !== FSVersionEnum.FS20) return null;

    const needsApproval = approval.approvalRequired && !approval.approvedAt && !approval.deniedAt && totalSent === 0;

    const isRejected = approval.approvalRequired && !approval.approvedAt && approval.deniedAt && totalSent === 0;

    const readyToBeSent = approval.approvalRequired && approval.approvedAt && !approval.deniedAt && totalSent === 0;

    if (totalCount === 0) return null;

    if (needsApproval) {
      const deadlineText = approval.deadline ? DateHelper.getDateTime(approval.deadline) : null;
      return (
        <Tooltip
          title={
            deadlineText
              ? t('invoice:invoiceMissionPage.needsApprovalWithDeadline', { date: deadlineText })
              : t('invoice:invoiceMissionPage.needsApproval')
          }
          arrow
          placement='left'
        >
          <Error color='error' />
        </Tooltip>
      );
    }

    if (isRejected) {
      return (
        <Tooltip title={t('invoice:invoiceMissionPage.rejectedSending')} arrow placement='left'>
          <Cancel color='error' />
        </Tooltip>
      );
    }

    if (readyToBeSent || (!approval.approvalRequired && totalSent === 0)) {
      return (
        <Tooltip title={t('invoice:invoiceMissionPage.readyTosend')} arrow placement='left'>
          <WatchLater color='warning' />
        </Tooltip>
      );
    }

    if (totalSent > 0 && totalSent < totalCount) {
      return (
        <Tooltip
          title={t('invoice:invoiceMissionPage.sendingNotFullfilled', {
            totalSentInvoices: totalSent,
            totalInvoices: totalCount,
          })}
          arrow
          placement='left'
        >
          <Warning color='warning' />
        </Tooltip>
      );
    }

    if (totalCount > 0 && totalSent >= totalCount) {
      return (
        <Tooltip title={t('invoice:invoiceMissionPage.sendingComplete')} arrow placement='left'>
          <CheckCircle color='success' />
        </Tooltip>
      );
    }

    return null;
  };
  const renderStatusIconFromData = (dataList: InvoiceMissionDataList, source: FSVersionEnum) => {
    return renderMissionStatusIcon(dataList.approval, dataList.totalCountSent, dataList.totalCount, source);
  };

  const columns: GridColDef[] = [
    { field: 'missionName', headerName: t('invoice:Designation'), flex: 1.75 },
    {
      field: 'date',
      headerName: t('invoice:date'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDate(params.row.date)}</>;
      },
    },
    {
      field: 'totalInvoiceCount',
      headerName: t('invoice:invoiceMissionPage.invoiceCount'),
      sortable: false,
      flex: 0.7,
      headerAlign: 'center',
      align: 'center',
    },
    {
      field: InvoiceSendingTypeStringEnum.invoice,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      headerName: t('invoice:invoiceMissionDetailTabs.mainSending'),
      flex: 0.7,
      valueGetter: (params) => params.row.dataList?.find((d) => d.sendingType === InvoiceSendingTypeStringEnum.invoice) ?? null,
      renderCell: (params) => (params.value ? renderStatusIconFromData(params.value, params.row.source) : null),
    },
    {
      field: InvoiceSendingTypeStringEnum.reminder1,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      headerName: t('invoice:invoiceMissionDetailTabs.reminderOne'),
      flex: 0.7,
      valueGetter: (params) => params.row.dataList?.find((d) => d.sendingType === InvoiceSendingTypeStringEnum.reminder1) ?? null,
      renderCell: (params) => (params.value ? renderStatusIconFromData(params.value, params.row.source) : null),
    },
    {
      field: InvoiceSendingTypeStringEnum.reminder2,
      sortable: false,
      headerAlign: 'center',
      align: 'center',
      headerName: t('invoice:invoiceMissionDetailTabs.reminderTwo'),
      flex: 0.7,
      valueGetter: (params) => params.row.dataList?.find((d) => d.sendingType === InvoiceSendingTypeStringEnum.reminder2) ?? null,
      renderCell: (params) => (params.value ? renderStatusIconFromData(params.value, params.row.source) : null),
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<InvoiceMissionResponse>
        autoHeight
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={(newSelectionModel) => {
          setSelectionModel(newSelectionModel);
        }}
        selectionModel={selectionModel}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
          }
        }}
      />
    </Box>
  );
};

export default InvoiceMissionDataGrid;
